import React from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import ExternalSourceSection from "./ExternalSourceSection";
import InternalSourceSection from "./InternalSourceSection";
import FastAPI from "../../api/FastAPI";
import { FAST_TYPE } from "../../api/MediaAPI.types";
import "../../css/admin/content-page.scss";
import SegmentedControl from "../utils/SegmentedControl/SegmentedControl";

import type { JSX } from "react";

import type {
  FastType,
  GetMediaResponse,
  MediaUpdated,
} from "../../api/MediaAPI.types";

interface ContentFASTProps {
  content: GetMediaResponse;
  onItemUpdate: (item: MediaUpdated) => void;
}

export default function ContentFAST({
  content,
  onItemUpdate,
}: ContentFASTProps): JSX.Element {
  const { t } = useTranslation("editor-content");

  const getBroadcastsQuery = FastAPI.generateQuery({
    fn: FastAPI.getBroadcasts,
    params: [],
  });

  const { data: broadcastsData, isLoading } = useQuery(getBroadcastsQuery);

  const tabs = [
    { label: t(FAST_TYPE.INTERNAL), value: FAST_TYPE.INTERNAL },
    { label: t(FAST_TYPE.EXTERNAL), value: FAST_TYPE.EXTERNAL },
  ];

  const handleFastTypeChange = (value: FastType): void => {
    switch (value) {
      case FAST_TYPE.INTERNAL:
        onItemUpdate({
          ...content,
          external_stream: null,
          fast: FAST_TYPE.INTERNAL,
        });
        break;
      case FAST_TYPE.EXTERNAL:
        onItemUpdate({
          ...content,
          fast_source: null,
          fast: FAST_TYPE.EXTERNAL,
        });
        break;
      default:
        break;
    }
  };

  const fastType = content.fast || FAST_TYPE.EXTERNAL;
  const isExternal = fastType === FAST_TYPE.EXTERNAL;
  const isSwitchDisabled =
    Boolean(isExternal && content.external_stream?.uuid) || !broadcastsData;

  return (
    <div className="editor-form-external-container editor-form-section">
      <div className="editor-form-section">
        <div className="editor-form-section-title flex flex-col gap-2">
          <span className="external-type">{t("source")}</span>
          <SegmentedControl
            disabled={isSwitchDisabled}
            onChange={handleFastTypeChange}
            selected={fastType || FAST_TYPE.EXTERNAL}
            tabs={tabs}
          />
        </div>
      </div>
      {isExternal ? (
        <ExternalSourceSection content={content} onItemUpdate={onItemUpdate} />
      ) : (
        <InternalSourceSection
          broadcastList={broadcastsData?.data ?? []}
          content={content}
          isLoading={isLoading}
          onItemUpdate={onItemUpdate}
        />
      )}
    </div>
  );
}
